import _, { isArray, isBoolean } from 'lodash';
import {
  RecipeFieldEnum as Fields,
  IngredientFieldEnum as IngredientFields,
  UserModifiedEnum as UserModifiedFields,
  PreparationInstructionEnum,
} from 'Common/FieldEnum';
import {
  convertToCups,
  convertToGrams,
} from 'Pages/manageingredient/helpers/ingredient';
import { COMPONENTS } from 'Common/Components/NutritionalHover/constants/constants';
import {
  BASE_WEIGHT_UNITS,
  BASE_VOLUME_UNITS,
  WEIGHT_UNITS,
  GRAMS_IN_LBS,
  GRAMS_IN_OZ,
} from 'Pages/manageingredient/helpers/Constants';

export const convertMNU_RecipeToRecipe = (MNU_Recipe) => {
  let newMNU_Recipe = { ...MNU_Recipe };
  if (newMNU_Recipe[Fields.Ingredients]) {
    newMNU_Recipe[Fields.Ingredients] = newMNU_Recipe[Fields.Ingredients].map(
      (ingredient) => {
        if (!ingredient[IngredientFields.Quantity2]) {
          ingredient[IngredientFields.Quantity2] = '';
        }

        return ingredient;
      }
    );
  }

  newMNU_Recipe.RecipeCategoryId = parseInt(newMNU_Recipe.RecipeCategoryId, 10);
  newMNU_Recipe[Fields.ServingTemperatureId] = parseInt(
    newMNU_Recipe[Fields.ServingTemperatureId],
    10
  );
  newMNU_Recipe[Fields.RecipeSourceId] = parseInt(
    newMNU_Recipe[Fields.RecipeSourceId],
    10
  );
  newMNU_Recipe[Fields.LeftoverDays] = parseInt(
    newMNU_Recipe[Fields.LeftoverDays],
    10
  );
  newMNU_Recipe[Fields.HACCPId] = parseInt(newMNU_Recipe[Fields.HACCPId], 10);
  newMNU_Recipe[Fields.GroupId] = parseInt(newMNU_Recipe[Fields.GroupId], 10);
  newMNU_Recipe[Fields.TotalVeg] = addVegetables(newMNU_Recipe);

  return newMNU_Recipe;
};

export const convertRecipeToMNU_Recipe = (recipe) => {
  let newRecipe = _.cloneDeep({ ...recipe });
  if (newRecipe[Fields.Ingredients]) {
    newRecipe[Fields.Ingredients] = newRecipe[Fields.Ingredients].map(
      (ingredient) => {
        if (!ingredient[IngredientFields.Quantity1]) {
          ingredient[IngredientFields.Quantity1] = 0;
        }
        if (!ingredient[IngredientFields.Quantity2]) {
          ingredient[IngredientFields.Quantity2] = 0;
        }

        return ingredient;
      }
    );
  }

  /** Remove ingredients from recipe instructions if not in recipe's list of ingredients */
  const ingredients = newRecipe[Fields.Ingredients] || [];
  const instructions = _.get(newRecipe, [Fields.PreparationInstructions], []);
  const updatedInstructions = instructions.map((instruction) => {
    const prepIngs = instruction[PreparationInstructionEnum.Ingredients] || [];
    return {
      ...instruction,
      [PreparationInstructionEnum.Ingredients]: prepIngs.filter((prepIng) =>
        ingredients.some(
          (i) => i[Fields.Id] === prepIng[PreparationInstructionEnum.Id]
        )
      ),
    };
  });
  newRecipe[Fields.PreparationInstructions] = updatedInstructions;

  return newRecipe;
};

export const updateNutritionFacts = (recipe, nutritionFacts) => {
  let newNutritionFacts = { ...nutritionFacts };
  if (
    recipe[Fields.UserModifiedSections] &&
    recipe[Fields.UserModifiedSections][UserModifiedFields.Allergens]
  ) {
    delete newNutritionFacts[Fields.Allergens];
  }

  if (
    recipe[Fields.UserModifiedSections] &&
    recipe[Fields.UserModifiedSections][UserModifiedFields.MealComponents]
  ) {
    delete newNutritionFacts[Fields.WeightPerServing];
    delete newNutritionFacts[Fields.MeatAlt];
    delete newNutritionFacts[Fields.Fruit];
    delete newNutritionFacts[Fields.FruitJuice];
    delete newNutritionFacts[Fields.GrainDessert];
    delete newNutritionFacts[Fields.EnrichedGrain];
    delete newNutritionFacts[Fields.WholeGrain];
    delete newNutritionFacts[Fields.TotalGrain];
    delete newNutritionFacts[Fields.LegumesVeg];
    delete newNutritionFacts[Fields.AdditionalVeg];
    delete newNutritionFacts[Fields.OtherVeg];
    delete newNutritionFacts[Fields.DarkGreenVeg];
    delete newNutritionFacts[Fields.RedOrangeVeg];
    delete newNutritionFacts[Fields.StarchyVeg];
    delete newNutritionFacts[Fields.TotalVeg];
    delete newNutritionFacts[Fields.VegJuice];
    delete newNutritionFacts[Fields.Milk];
    delete newNutritionFacts[Fields.Milk_LowFatFlavored];
    delete newNutritionFacts[Fields.Milk_LowFatUnflavored];
    delete newNutritionFacts[Fields.Milk_ReducedUnflavored];
    delete newNutritionFacts[Fields.Milk_ReducedFlavored];
    delete newNutritionFacts[Fields.Milk_WholeUnflavored];
    delete newNutritionFacts[Fields.Milk_WholeFlavored];
    delete newNutritionFacts[Fields.Milk_SkimFlavored];
    delete newNutritionFacts[Fields.Milk_SkimUnflavored];
  }

  return { ...recipe, ...newNutritionFacts };
};

export const addVegetables = (data) => {
  let total = 0;

  total = _.add(total, Number(data[Fields.OtherVeg]) || 0);
  total = _.add(total, Number(data[Fields.AdditionalVeg]) || 0);
  total = _.add(total, Number(data[Fields.LegumesVeg]) || 0);
  total = _.add(total, Number(data[Fields.StarchyVeg]) || 0);
  total = _.add(total, Number(data[Fields.DarkGreenVeg]) || 0);
  total = _.add(total, Number(data[Fields.RedOrangeVeg]) || 0);

  return total;
};

export const hasUserModified = (original, updated) => {
  const updatedKeys = Object.keys(COMPONENTS).map((key) => {
    return original[key] !== updated[key];
  });

  return updatedKeys.filter(Boolean).length > 0;
};

export const getIngredientWeightInGrams = (ingredient) => {
  const measure = ingredient.Measure1;
  const quantity = Number(ingredient.Quantity1) || 0;
  const baseMeasures = ingredient.BaseMeasures || [];
  let grams = 0;

  if (BASE_WEIGHT_UNITS.includes(_.toUpper(measure))) {
    // find a conversion for weight units
    const baseMeasure = baseMeasures.find((bm) =>
      BASE_WEIGHT_UNITS.includes(_.toUpper(bm.Measurement))
    );
    if (baseMeasure) {
      const baseMeasureToGrams = convertToGrams(1, baseMeasure.Measurement);
      const measureToGrams = convertToGrams(1, measure);

      // calculate the ratio of the base measure
      const ratio = _.divide(measureToGrams, baseMeasureToGrams);

      const gramsRatio = _.multiply(
        ratio,
        convertToGramsRatio(
          baseMeasure.Amount,
          baseMeasure.Weight,
          baseMeasure.BaseMeasure
        )
      );

      grams = _.multiply(quantity, gramsRatio);
    } else {
      // if no conversion for weight units, use the standard conversion
      grams = convertToGrams(quantity, measure);
    }
  } else if (BASE_VOLUME_UNITS.includes(measure)) {
    // find a base measure that is a volume measure
    const baseMeasure = baseMeasures.find((bm) =>
      BASE_VOLUME_UNITS.includes(bm.Measurement)
    );

    if (baseMeasure) {
      // convert base measure to cups
      const baseMeasurementToCupRatio = convertToCups(measure.Measurement);
      // convert selected measure to cups
      const measureToCupRatio = convertToCups(measure);

      // get ratio between selected measure and base measure
      const ratio = _.divide(measureToCupRatio, baseMeasurementToCupRatio);

      const volumeToGramsRatio = _.multiply(
        ratio,
        convertToGramsRatio(
          baseMeasure.Amount,
          baseMeasure.Weight,
          baseMeasure.BaseMeasure
        )
      );

      // calculate volume to grams with ratio and quantity
      grams = _.multiply(quantity, volumeToGramsRatio);
    }
  } else {
    // find non standard conversion, conversion that is not based on weight or volume
    const baseMeasure = baseMeasures.find(
      (bm) => _.toUpper(bm.Measurement) === _.toUpper(measure)
    );
    if (baseMeasure) {
      grams = _.multiply(
        quantity,
        convertToGramsRatio(
          baseMeasure.Amount,
          baseMeasure.Weight,
          baseMeasure.BaseMeasure
        )
      );
    }
  }

  return grams;
};

const convertToGramsRatio = (amount, weight, unit) => {
  const ratio = _.divide(weight, amount);
  switch (_.toUpper(unit)) {
    case WEIGHT_UNITS.OZ:
      return _.multiply(ratio, GRAMS_IN_OZ);
    case WEIGHT_UNITS.LB:
      return _.multiply(ratio, GRAMS_IN_LBS);
    case WEIGHT_UNITS.GRAMS:
    default:
      return ratio;
  }
};

export const calculateServingSizeWeight = (
  ingredients = [],
  numberOfPortions = 1
) => {
  let weightOfIngredients = 0;
  const portions = Number(numberOfPortions) || 1;

  ingredients.forEach((ing) => {
    weightOfIngredients += getIngredientWeightInGrams(ing);
  });

  const servingSizeWeight = _.divide(weightOfIngredients, portions);
  return Number(servingSizeWeight.toFixed(4));
};

export const cleanUpRecipeValues = (recipe) => {
  return _.mapValues(recipe, function (value, key, o) {
    return value !== '' &&
      value !== null &&
      !isNaN(value) &&
      !isArray(value) &&
      !isBoolean(value)
      ? String(parseFloat(value))
      : value;
  });
};

export const mergeRecipeAttributes = (currentAttributes, attributesFromAPI) => {
  const rawAttributes = [...currentAttributes, ...attributesFromAPI];
  const mergedAttributes = [];

  rawAttributes.forEach((attribute) => {
    const alreadyInList = mergedAttributes.find((x) => x.Id === attribute.Id);

    if (!alreadyInList) {
      console.log(attribute);
      const foundAPIAttribute = attributesFromAPI.find(
        (x) => x.Id === attribute.Id
      );
      if (foundAPIAttribute) {
        mergedAttributes.push(foundAPIAttribute);
      } else {
        mergedAttributes.push(attribute);
      }
    }
  });

  return mergedAttributes;
};



// WEBPACK FOOTER //
// ./src/pages/managerecipe/helpers/recipe.js